<script lang="ts">
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import * as m from '$paraglide/messages';
	import { createRangeCalendar, type CreateRangeCalendarProps, type DateRange } from '@melt-ui/svelte';
	import { createEventDispatcher } from 'svelte';

	export let calendarConfig: CreateRangeCalendarProps | undefined = {};
	export let dates: DateRange = { start: undefined, end: undefined };

	const dispatch = createEventDispatcher<{
		cancelCalendarClick: void;
		validateCalendarClick: { selectedDates: DateRange | undefined };
	}>();

	const {
		elements: { calendar, cell, grid, heading, nextButton, prevButton },
		states: { months, headingValue, weekdays, value, startValue, endValue },
		helpers: { isDateDisabled, isDateUnavailable },
	} = createRangeCalendar({
		...calendarConfig,
	});

	export function clearState() {
		$value = { start: undefined, end: undefined };
		$startValue = undefined;
		$endValue = undefined;
	}

	$: dates = { start: $startValue, end: $endValue };

	const activeDateClass = 'active:bg-hotel active:border-transparent active:text-warning-weakest';
	const hoverDateClass = 'hover:bg-hotel-weaker hover:border-transparent hover:text-coloredneutral';
	const focusDateClass
		= 'focus:shadow-calendar-focus focus:text-coloredneutral focus:border-transparent focus:bg-hotel-weak focus:outline-unset';
	const dateClass
		= 'flex h-6 w-6 cursor-pointer select-none items-center justify-center p-4 border-0 text-sm';

	function handleValidation(event: MouseEvent) {
		event.stopPropagation();
		const selectedDates = $value.start && $value.end ? $value : { start: $startValue, end: $endValue };
		dispatch('validateCalendarClick', { selectedDates });
	}

	function handleCancelation(event: MouseEvent) {
		event.stopPropagation();
		clearState();
		dispatch('cancelCalendarClick');
	}
</script>

<!-- eslint-disable svelte/require-store-reactive-access -->
<div
	class="flex-inline flex-col justify-start items-start h-full z-infront rd-2 bg-hotel-minimal p-4 text-coloredneutral shadow-smooth-bottom-3 gap-6"
	{...$calendar}
	use:calendar
>
	<header class="flex flex-col justify-start items-start w-full gap-6">
		<div class="flex-inline justify-between items-center self-stretch">
			<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
			<div class="text-md font-600" {...$heading} use:heading>
				{$headingValue}
			</div>
			<div class="flex justify-start items-start gap-2">
				<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
				<div
					class="flex justify-between items-center cursor-pointer p-2.5"
					{...$prevButton}
					use:prevButton
					aria-disabled={$prevButton['aria-disabled'] ? 'true' : undefined}
				>
					<Icon icon="chevron-left" class="text-3.5" />
				</div>
				<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
				<div
					class="flex justify-between items-center cursor-pointer p-2.5"
					{...$nextButton}
					use:nextButton
					aria-disabled={$nextButton['aria-disabled'] ? 'true' : undefined}
				>
					<Icon icon="chevron-right" class="text-3.5" />
				</div>
			</div>
		</div>
	</header>
	<div class="flex flex-col justify-start items-start gap-2">
		<!-- eslint-disable-next-line svelte/require-each-key -->
		{#each $months as month}
			<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
			<table
				class="w-full gap-1"
				{...$grid}
				use:grid
			>
				<thead aria-hidden="true">
					<tr>
						<!-- eslint-disable-next-line svelte/require-each-key -->
						{#each $weekdays as day}
							<th class="text-sm font-600">
								<div class="flex h-6 w-6 items-center justify-center p-4">
									{day}
								</div>
							</th>
						{/each}
					</tr>
				</thead>
				<tbody>
					<!-- eslint-disable-next-line svelte/require-each-key -->
					{#each month.weeks as weekDates}
						<tr>
							<!-- eslint-disable-next-line svelte/require-each-key -->
							{#each weekDates as date}
								<td
									role="gridcell"
									aria-disabled={$isDateDisabled(date) || $isDateUnavailable(date)}
								>
									<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
									<div
										class="{dateClass} {hoverDateClass} {activeDateClass} {focusDateClass}"
										{...$cell(date, month.value)}
										use:cell
									>
										{date.day}
									</div>
								</td>
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
		{/each}
		<div class="flex-inline justify-between items-center gap-2 self-stretch">
			<Button
				class="text-md text-hotel p-0"
				text={m.calendar__cancel()}
				variant="ghost"
				onclick={handleCancelation}
			/>
			<Button
				class="text-md text-white bg-hotel"
				text={m.calendar__validate()}
				variant="filled"
				onclick={handleValidation}
			/>
		</div>
	</div>
</div>

<style>
	[data-melt-calendar-cell][data-disabled] {
		--uno: pointer-events-none text-coloredneutral-weak;
	}

	[data-melt-calendar-cell][data-unavailable] {
		--uno: pointer-events-none;
	}

	[data-melt-calendar-cell][data-outside-visible-months] {
		--uno: pointer-events-none cursor-default text-coloredneutral-weak;
	}
	[data-melt-calendar-cell][data-outside-visible-months]:hover,
	[data-melt-calendar-cell][data-outside-month]:hover {
		--uno: bg-transparent;
	}

	[data-melt-calendar-cell][data-outside-month] {
		--uno: pointer-events-none cursor-default opacity-0;
	}

	td:has([data-melt-calendar-cell][data-highlighted]:not([aria-disabled])) {
		--uno: bg-hotel-weak text-coloredneutral border-0;
	}

	td:has([data-melt-calendar-cell][data-selected]:not([aria-disabled])) {
		--uno: bg-hotel-weak shadow-none text-warning-weakest;
	}
	[data-melt-calendar-cell][data-selected]{
		--uno: bg-transparent border-0;
	}
	[data-melt-calendar-prevbutton][data-disabled] {
		--uno: cursor-not-allowed pointer-events-none text-coloredneutral-weak;
	}

	[data-melt-calendar-nextbutton][data-disabled] {
		--uno: cursor-not-allowed pointer-events-none text-coloredneutral-weak;
	}

	[data-melt-calendar-cell][data-selected][data-selection-start]:not([aria-disabled]),
	[data-melt-calendar-cell][data-selected][data-selection-end]:not([aria-disabled]) {
		--uno: text-warning-weakest
	}

	td:has([data-melt-calendar-cell][data-selected]:not([aria-disabled])) {
		--uno: bg-hotel-weak text-coloredneutral border-0;
	}
	td:has([data-melt-calendar-cell][data-selected][data-selection-start]:not([aria-disabled])),
	td:has([data-melt-calendar-cell][data-selected][data-selection-end]:not([aria-disabled])) {
		--uno: bg-hotel-strong;
	}

	td:has([data-melt-calendar-cell][data-selected][data-selection-end]:not([aria-disabled])) {
		--uno:  rounded-tr-1 rounded-br-1;
	}
	td:has([data-melt-calendar-cell][data-selected][data-selection-start]:not([aria-disabled])) {
		--uno: rounded-tl-1 rounded-bl-1;
	}

	[data-melt-calendar-heading]::first-letter {
		text-transform: uppercase;
	}
	table[data-melt-calendar-grid] {
		border-collapse: separate;
		border-spacing: 0 2px;
	}
</style>
